import { useCallback } from 'react';
import { useIntl } from 'react-intl';

import { useAppDispatch } from '@verantos-dev/app-state-base';
import {
  addResearchAssistantMessages,
  COHORT_ACTIONS,
} from '@verantos-dev/app-state-research-assistant';
import { type MessageLegacy } from '@verantos-dev/app-state-research-assistant';
import { type MessageFeedback } from '@verantos-dev/shared-components';
import {
  ResearchAssistantMessageEnumLegacy,
  type AVProgressAction,
  type AVProgressItem,
  type AVProgressStatus,
} from '@verantos-dev/types-common';

type Message = {
  messageId?: string;
  userPrompt?: string;
  summary?: string;
  createdAt: string;
  modelVersion: string;
  isUserGeneratedDsl?: boolean;
  cohortAction?: string;
  dsl?: string;
  omopMetadata?: {
    session_id: string;
    file_name: string;
    original_file_name?: string;
    total_codes?: number;
    s3_uri?: string;
    status?: string;
    mapping_summary?: unknown;
  };
  conceptGroupPreview?: {
    previewId: string;
    conceptGroupName: string;
    totalCount?: number;
    domainBreakdown?: Record<string, number>;
  };
  feedback?: MessageFeedback;
  // TODO: cleanup with analytical_variables_in_cohort_builder ff
  progressData?: {
    step: string;
    message: string;
    av_progress: Array<{
      av_index: number;
      name: string;
      action: string;
      status: string;
      retry_message?: string;
      total_count?: number;
    }>;
  };
};

export const useHandlePopulateChatHistory = () => {
  const dispatch = useAppDispatch();
  const intl = useIntl();

  // useCallback minimizes the number of times we dispatch downstream effects
  const constructMessages = useCallback(
    (messages: Array<Message>) => {
      return messages.reduce<Array<MessageLegacy>>((acc, message) => {
        const lastMessage = acc.slice(-1)[0];
        if (
          lastMessage &&
          lastMessage.modelVersion &&
          lastMessage.modelVersion !== message.modelVersion
        ) {
          const textToDisplay = intl.formatMessage(
            {
              id: 'projects.cohortBuilderPage.researchAssistant.updateModelVersionInfoMessage',
            },
            {
              oldModelVersion: lastMessage.modelVersion,
              newModelVersion: message.modelVersion,
            }
          );
          acc.push({
            message: textToDisplay,
            timestamp: message.createdAt,
            currentUser: '',
            isCurrentUser: false,
            type: ResearchAssistantMessageEnumLegacy.MODEL_CHANGE,
            modelVersion: message.modelVersion,
          });
        }
        if (!message.isUserGeneratedDsl && !message.cohortAction) {
          if (message.userPrompt) {
            acc.push({
              message: message.userPrompt,
              timestamp: message.createdAt,
              currentUser: 'user',
              isCurrentUser: false,
              modelVersion: message.modelVersion,
              messageId: message.messageId,
            });
          }
          // Restore progress messages from history (persisted in blocks[5] of assistant message)
          // TODO: cleanup with analytical_variables_in_cohort_builder ff
          if (message.progressData && message.progressData.av_progress?.length > 0) {
            const avProgressItems: AVProgressItem[] = message.progressData.av_progress.map(
              (av) => ({
                avIndex: av.av_index,
                name: av.name,
                action: av.action as AVProgressAction,
                status: av.status as AVProgressStatus,
                retryMessage: av.retry_message,
              })
            );
            acc.push({
              message: '',
              currentUser: 'research_assistant',
              isCurrentUser: false,
              type: ResearchAssistantMessageEnumLegacy.PROGRESS,
              progressData: {
                step: message.progressData.step,
                message: message.progressData.message,
                avProgress: avProgressItems,
                isComplete: true,
              },
            });
          }

          // Restore concept group review messages from history
          if (message.conceptGroupPreview) {
            const preview = message.conceptGroupPreview;
            acc.push({
              message: intl.formatMessage(
                { id: 'projects.researchAssistant.conceptGroupReview.message' },
                { conceptGroupName: preview.conceptGroupName }
              ),
              timestamp: message.createdAt,
              currentUser: 'research_assistant',
              isCurrentUser: false,
              modelVersion: message.modelVersion,
              type: ResearchAssistantMessageEnumLegacy.CONCEPT_GROUP_REVIEW,
              messageId: message.messageId,
              feedback: message.feedback,
              conceptGroupReviewData: {
                conceptGroupName: preview.conceptGroupName,
                concepts: [],
                previewId: preview.previewId,
                totalCount: preview.totalCount,
                domainBreakdown: preview.domainBreakdown,
              },
            });
          } else if (message.summary) {
            if (message.omopMetadata) {
              // Store OMOP metadata to attach to the following detailed RA message
              (acc as any).pendingOmopMetadata = message.omopMetadata;
            } else {
              const pendingMetadata = (acc as any).pendingOmopMetadata;
              if (pendingMetadata) {
                acc.push({
                  message: message.summary || '',
                  timestamp: message.createdAt,
                  currentUser: 'research_assistant',
                  isCurrentUser: false,
                  modelVersion: message.modelVersion,
                  type: ResearchAssistantMessageEnumLegacy.OMOP_MAPPING_RESULTS,
                  omopMetadata: pendingMetadata,
                  messageId: message.messageId,
                  feedback: message.feedback,
                });
                delete (acc as any).pendingOmopMetadata;
              } else {
                acc.push({
                  message: message.summary || '',
                  timestamp: message.createdAt,
                  currentUser: 'research_assistant',
                  isCurrentUser: false,
                  modelVersion: message.modelVersion,
                  messageId: message.messageId,
                  feedback: message.feedback,
                });
              }
            }
          }
        }
        if (message.cohortAction) {
          if (message.cohortAction === COHORT_ACTIONS.SAVE) {
            const textToDisplay = intl.formatMessage({
              id: 'projects.cohortBuilderPage.researchAssistant.savedInfoMessage',
            });
            if (lastMessage && lastMessage.message === textToDisplay) {
              acc[acc.length - 1].timestamp = message.createdAt;
            } else {
              acc.push({
                message: textToDisplay,
                timestamp: message.createdAt,
                currentUser: '',
                isCurrentUser: false,
                type: ResearchAssistantMessageEnumLegacy.SAVE,
                modelVersion: message.modelVersion,
              });
            }
          }
          if (message.cohortAction === COHORT_ACTIONS.PROCESS) {
            const textToDisplay = intl.formatMessage({
              id: 'projects.cohortBuilderPage.researchAssistant.processedInfoMessage',
            });
            if (lastMessage && lastMessage.message === textToDisplay) {
              acc[acc.length - 1].timestamp = message.createdAt;
            } else {
              acc.push({
                message: textToDisplay,
                timestamp: message.createdAt,
                currentUser: '',
                isCurrentUser: false,
                type: ResearchAssistantMessageEnumLegacy.PROCESS,
                modelVersion: message.modelVersion,
              });
            }
          }
          if (message.cohortAction === COHORT_ACTIONS.REVERT) {
            const textToDisplay = intl.formatMessage({
              id: 'projects.cohortBuilderPage.researchAssistant.revertedInfoMessage',
              defaultMessage: 'You reverted the unsaved changes',
            });
            if (lastMessage && lastMessage.message === textToDisplay) {
              acc[acc.length - 1].timestamp = message.createdAt;
            } else {
              acc.push({
                message: textToDisplay,
                timestamp: message.createdAt,
                currentUser: '',
                isCurrentUser: false,
                type: ResearchAssistantMessageEnumLegacy.REVERT,
                modelVersion: message.modelVersion,
              });
            }
          }
        }
        if (message.isUserGeneratedDsl) {
          const textToDisplay = intl.formatMessage({
            id: 'projects.cohortBuilderPage.researchAssistant.updateInfoMessage',
          });
          if (lastMessage && lastMessage.message === textToDisplay) {
            acc[acc.length - 1].timestamp = message.createdAt;
          } else {
            acc.push({
              message: textToDisplay,
              timestamp: message.createdAt,
              currentUser: '',
              isCurrentUser: false,
              type: ResearchAssistantMessageEnumLegacy.INFO,
              modelVersion: message.modelVersion,
            });
          }
        }
        return acc;
      }, []);
    },
    [intl]
  );

  // useCallback minimizes the number of times we dispatch downstream effects
  const populateMessages = useCallback(
    (messages: Array<Message>) => {
      dispatch(addResearchAssistantMessages(constructMessages(messages)));
    },
    [dispatch, constructMessages]
  );

  return { populateMessages };
};
